import React from 'react';

export interface PickupResultData {
  requested: number;
  already_requested: number;
  total_cod: number;
  pickup_time?: string;
  results: Array<{
    order?: string | number;
    customer_name?: string;
    consignment_id?: string;
    cod_amount?: number;
    pickup_failed?: boolean;
    pickup_already_pending?: boolean;
    re_requested?: boolean;
    already_requested?: boolean;
  }>;
  pickup_api_success?: boolean;
  pickup_api_error?: string;
  pickup_id?: number;
  previously_requested?: number;
  pickup_already_pending?: boolean;
}

interface PickupResultModalProps {
  result: PickupResultData | null;
  onClose: () => void;
}

export const PickupResultModal: React.FC<PickupResultModalProps> = ({
  result,
  onClose,
}) => {
  if (!result) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(4px)',
      padding: '16px',
    }}>
      <div
        className="modal-content om-modal-window"
        style={{
        backgroundColor: 'white',
        borderRadius: '14px',
        width: '100%',
        maxWidth: '720px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '22px' }}>🚚</span>
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700' }}>Pickup Request — Manifest</h2>
              <p style={{ margin: 0, fontSize: '12px', opacity: 0.85 }}>
                ✅ {result.requested} requested
                {result.previously_requested && result.previously_requested > 0 ? (
                  <> &nbsp;·&nbsp; 🔄 {result.previously_requested} re-requested</>
                ) : null}
                &nbsp;·&nbsp; Total COD: ৳{result.total_cod.toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontSize: '20px',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
          {/* Info banner: pickup already pending */}
          {result.pickup_already_pending && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>ℹ️</span>
              <div>
                <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '13px', marginBottom: '4px' }}>Pickup Already Scheduled</div>
                <div style={{ color: '#1d4ed8', fontSize: '12px', lineHeight: '1.5' }}>A pending pickup request already exists for your address. These orders have been marked and will be included in the existing pickup.</div>
              </div>
            </div>
          )}

          {/* API error banner */}
          {result.pickup_api_error && !result.pickup_already_pending && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>❌</span>
              <div>
                <div style={{ fontWeight: 700, color: '#991b1b', fontSize: '13px', marginBottom: '4px' }}>Pickup Request Failed</div>
                <div style={{ color: '#b91c1c', fontSize: '12px', lineHeight: '1.5' }}>{result.pickup_api_error}</div>
              </div>
            </div>
          )}

          {/* Summary cards */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#111827' }}>{result.requested + result.already_requested}</div>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Total Parcels</div>
            </div>
            <div style={{ flex: 1, background: '#ede9fe', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#5b21b6' }}>৳{result.total_cod.toLocaleString()}</div>
              <div style={{ fontSize: '11px', color: '#7c3aed', fontWeight: '600' }}>Total COD</div>
            </div>
            <div style={{ flex: 1, background: result.pickup_api_error ? '#fef2f2' : '#d1fae5', borderRadius: '10px', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: result.pickup_api_error ? '#991b1b' : '#065f46' }}>{result.requested}</div>
              <div style={{ fontSize: '11px', color: result.pickup_api_error ? '#b91c1c' : '#059669', fontWeight: '600' }}>Newly Requested</div>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>#</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Order</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Customer</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Consignment ID</th>
                <th style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>COD</th>
                <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: r.already_requested ? '#f5f3ff' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '9px 12px', color: '#6b7280', fontSize: '12px' }}>{i + 1}</td>
                  <td style={{ padding: '9px 12px', fontWeight: '600', color: '#111827' }}>{r.order}</td>
                  <td style={{ padding: '9px 12px', color: '#374151' }}>{r.customer_name || '—'}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#1d4ed8', fontWeight: '700' }}>
                    {r.consignment_id || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                    ৳{r.cod_amount?.toLocaleString() || '—'}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    {r.pickup_failed ? (
                      <span style={{ background: '#fecaca', color: '#991b1b', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>❌ Failed</span>
                    ) : r.pickup_already_pending ? (
                      <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>ℹ️ Pending</span>
                    ) : r.re_requested ? (
                      <span style={{ background: '#dbeafe', color: '#1e40af', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>🔄 Re-requested</span>
                    ) : (
                      <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '12px', padding: '2px 10px', fontSize: '11px', fontWeight: '700' }}>✅ Requested</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 22px',
              background: '#111827',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PickupResultModal;
